// @givmo/api-client — silent donor device-timezone capture (C.3 donor-local receipt dating).
//
// Every donation-create POST body carries an additive OPTIONAL `donor_timezone` (the device's IANA
// zone key, e.g. "America/New_York") so the backend can date the donor's receipt in THEIR zone
// instead of the ET fallback — behind the backend's dark flag. Captured SILENTLY from the runtime:
// zero UI, the donor is never asked. The backend is a tolerant reader (invalid/absent → the ET
// fallback, never a 4xx), and capture must NEVER block a donation: any resolution failure returns
// `{}` so the key is OMITTED entirely (never a stray null on the wire) and checkout proceeds
// unchanged.

/** The wire field a donation-create body carries when the device zone resolved (C.3). */
export interface DonorTimezoneField {
  donor_timezone?: string;
}

/**
 * The donor's device IANA time zone as a spread-ready body fragment: `{ donor_timezone }` when the
 * runtime resolves a non-empty zone, `{}` otherwise (a throwing/absent `Intl` is swallowed — a
 * timezone failure must never block a donation). Spread into a donation-create body.
 *
 * @example sdk.donationsCreateUnauthenticatedDonation({ body: { ...base, ...donorTimezone() } })
 */
export function donorTimezone(): DonorTimezoneField {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === 'string' && zone.length > 0 ? { donor_timezone: zone } : {};
  } catch {
    return {};
  }
}
